import { FormProvider, useForm } from 'react-hook-form';
import { FormValue, OrderType, Type } from './type';
import { Link, useSearchParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { Pair } from '@/store/pairs';
import { ChartView } from './chart';
import { TrandingData } from './detail';
import { useShallow } from 'zustand/shallow';
import { usePrice } from '@/store/price';
import { ArrowDownIcon, ArrowUpAltIcon } from '@/icons';
import clsx from 'clsx';
import { formatDisplay } from '@/utils/format-display';
import { BuySell } from './buy-sell';
import { usePairsByIndex } from './hooks/usePairsByIndex';
import { useGetLeverage } from './hooks/useGetLeverage';
import { useTranslation } from 'react-i18next';

export function TradingPage() {
    const [searchParams] = useSearchParams();

    const pairIndex = useMemo(() => {
        return (
            searchParams.get('pairIndex') ||
            sessionStorage.getItem('pairIndex') ||
            '0'
        );
    }, [searchParams]);

    const { maxLeverage, initLeverage } = useGetLeverage(Number(pairIndex));

    const methods = useForm<FormValue>({
        defaultValues: {
            type: Type.Long,
            orderType: OrderType.Market,
            leverage: maxLeverage,
            limitPrice: '',
            pairIndex,
            amount: initLeverage.toString(),
            tp: '',
            sl: '',
            unit: 'USD',
        },
    });

    useEffect(() => {
        sessionStorage.setItem('pairIndex', pairIndex);
        methods.setValue('pairIndex', pairIndex);
    }, [methods, pairIndex]);
    const [tab, setTab] = useState<'price' | 'trading_data'>('price');

    const pair = usePairsByIndex(pairIndex);

    return (
        <FormProvider {...methods}>
            <div className="">
                <Header
                    tab={tab}
                    setTab={setTab}
                    pair={pair}
                    pairIndex={pairIndex}
                />
                <div className="relative overflow-hidden">
                    <ChartView />
                    {tab === 'trading_data' ? (
                        <TrandingData pairIndex={pairIndex} pair={pair} />
                    ) : null}
                </div>
                {tab === 'price' && <BuySell />}
            </div>
        </FormProvider>
    );
}

function Header(props: {
    tab: 'price' | 'trading_data';
    setTab: (t: 'price' | 'trading_data') => void;
    pairIndex: string;
    pair?: Pair;
}) {
    const { tab, setTab, pair, pairIndex } = props;

    const data = usePrice(useShallow((state) => state.data));
    const { t } = useTranslation();
    return (
        <div className="px-5 pt-6 pb-2">
            <div className="flex items-center justify-between mb-2">
                <Link to={'/market'} className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center shrink-0">
                        <img src={pair?.imageUrl} />
                    </div>
                    <div>
                        <div className="flex items-center gap-1 cursor-pointer">
                            <h1 className="text-xl font-bold text-slate-900">
                                {pair?.symbol}
                            </h1>
                            <ArrowDownIcon className="w-5 h-5" />
                        </div>
                        <p className="text-xs text-slate-500 font-medium">
                            {pair?.name}
                        </p>
                    </div>
                </Link>
            </div>

            <div className="flex items-center gap-6 border-b border-slate-100 mb-2">
                <button
                    className={clsx(
                        'cursor-pointer pb-2 text-sm font-bold  border-b-2 ',
                        {
                            'text-slate-900 border-slate-900': tab === 'price',
                            'text-slate-400 hover:text-slate-600 border-transparent hover:border-slate-200 transition-colors':
                                tab !== 'price',
                        }
                    )}
                    onClick={() => {
                        setTab('price');
                    }}
                >
                    {t('Price')}
                </button>
                <button
                    onClick={() => {
                        setTab('trading_data');
                    }}
                    className={clsx(
                        'cursor-pointer pb-2 text-sm font-bold  border-b-2 ',
                        {
                            'text-slate-900 border-slate-900': tab !== 'price',
                            'text-slate-400 hover:text-slate-600 border-transparent hover:border-slate-200 transition-colors':
                                tab == 'price',
                        }
                    )}
                >
                    {t('Trading Data')}
                </button>
            </div>
          
            <div className="mb-2">
                <div className="flex items-baseline gap-3">
                    <h2 className="text-[40px] font-bold tracking-tight text-slate-900 tabular-nums">
                        {formatDisplay(Number(data[pairIndex]?.price || 0), {
                            decimalToShow: pair?.decimalsToShow || 2,
                            minimumDecimalToShow: 2,
                        }) || '--'}
                    </h2>

                    <span
                        className={clsx(
                            'text-sm font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ',
                            {
                                'text-[#FF695F] bg-[#FF695F]/10':
                                    Number(data[pairIndex]?.price24h ?? 0) < 0,
                                'text-[#38F24A] bg-[#38F24A]/10':
                                    Number(data[pairIndex]?.price24h ?? 0) > 0,
                                'text-slate-900 bg-slate-900/10':
                                    Number(data[pairIndex]?.price24h ?? 0) == 0,
                            }
                        )}
                    >
                        <ArrowUpAltIcon
                            className={clsx('w-4 h-4', {
                                'text-[#38F24A]':
                                    Number(data[pairIndex]?.price24h ?? 0) > 0,
                                'text-[#FF695F] rotate-180':
                                    Number(data[pairIndex]?.price24h ?? 0) < 0,
                            })}
                        />
                        {formatDisplay(
                            Math.abs(Number(data[pairIndex]?.price24h ?? 0)),
                            {
                                decimalToShow: pair?.decimalsToShow ?? 5,
                                minimumDecimalToShow: 2,
                            }
                        )}
                        %
                    </span>
                </div>
            </div>
        </div>
    );
}
